import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "../context";

/* Meteor Shower Overlay */
const MeteorShowerOverlay = ({ overlay }) => {
  const meteors = useMemo(() => Array.from({ length: overlay.count || 15 }, (_, i) => ({
    delay: Math.random() * 8, dur: 0.6 + Math.random() * 0.8,
    x: Math.random() * 100, len: 30 + Math.random() * 60,
  })), [overlay.count]);
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-[8]">
      {meteors.map((m, i) => (
        <div key={i} className="absolute" style={{
          top: '-5%', left: `${m.x}%`, width: 2, height: m.len,
          background: `linear-gradient(180deg, transparent, ${overlay.color || '#FBBF24'}, transparent)`,
          transform: 'rotate(35deg)',
          animation: `weather-meteor ${m.dur}s linear ${m.delay}s infinite`,
          boxShadow: overlay.glow ? `0 0 8px ${overlay.color || '#FBBF24'}60` : 'none',
        }} />
      ))}
    </div>
  );
};

/* Rainbow Bridge Overlay */
const RainbowBridgeOverlay = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden z-[8]">
    <div className="absolute top-[5%] left-1/2 -translate-x-1/2 w-[80%] h-[50%] rounded-t-full" style={{
      background: "conic-gradient(from 180deg, rgba(239,68,68,0.15), rgba(249,115,22,0.15), rgba(234,179,8,0.15), rgba(34,197,94,0.15), rgba(59,130,246,0.15), rgba(139,92,246,0.15), rgba(236,72,153,0.15), transparent 55%)",
      filter: "blur(8px)",
      animation: "rainbow-bridge-appear 3s ease-out forwards",
    }} />
  </div>
);

/* Aurora Storm Overlay */
const AuroraStormOverlay = ({ overlay }) => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden z-[8]">
    {Array.from({ length: overlay.count || 5 }, (_, i) => (
      <div key={i} className="absolute w-full" style={{
        top: `${i * 12}%`, height: '20%',
        background: `linear-gradient(180deg, transparent, ${['rgba(34,211,238,0.12)', 'rgba(139,92,246,0.1)', 'rgba(52,211,153,0.1)', 'rgba(236,72,153,0.08)', 'rgba(59,130,246,0.1)'][i]}, transparent)`,
        animation: `aurora-storm-wave ${6 + i * 2}s ease-in-out ${i * -1.5}s infinite`,
      }} />
    ))}
  </div>
);

/* Star Rain Overlay */
const StarRainOverlay = ({ overlay }) => {
  const stars = useMemo(() => Array.from({ length: overlay.count || 30 }, (_, i) => ({
    x: Math.random() * 100, delay: Math.random() * 6,
    dur: 2 + Math.random() * 3, size: 3 + Math.random() * 4,
  })), [overlay.count]);
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-[8]">
      {stars.map((s, i) => (
        <div key={i} className="absolute" style={{
          top: '-3%', left: `${s.x}%`, width: s.size, height: s.size,
          background: overlay.color || '#FBBF24',
          clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
          animation: `weather-star-rain ${s.dur}s linear ${s.delay}s infinite`,
          filter: overlay.glow ? `drop-shadow(0 0 4px ${overlay.color || '#FBBF24'})` : 'none',
        }} />
      ))}
    </div>
  );
};

/* Cosmic Wave Overlay */
const CosmicWaveOverlay = ({ overlay }) => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden z-[8]">
    {Array.from({ length: overlay.count || 3 }, (_, i) => (
      <div key={i} className="absolute w-full" style={{
        top: `${20 + i * 25}%`, height: 4,
        background: `linear-gradient(90deg, transparent, ${overlay.color || '#A78BFA'}40, ${overlay.color || '#A78BFA'}60, ${overlay.color || '#A78BFA'}40, transparent)`,
        animation: `cosmic-wave-pass ${8 + i * 3}s linear ${i * 3}s infinite`,
        filter: overlay.glow ? `drop-shadow(0 0 8px ${overlay.color || '#A78BFA'}60)` : 'none',
      }} />
    ))}
  </div>
);

/* ====== DISPATCHER ====== */
const WeatherOverlay = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchWeather = () => {
      axios.get(`${API}/weather/overlay`).then(r => {
        if (r.data.has_overlay) setData(r.data);
        else setData(null);
      }).catch(() => {});
    };
    fetchWeather();
    const interval = setInterval(fetchWeather, 60000);
    return () => clearInterval(interval);
  }, []);

  if (!data || !data.overlay) return null;

  const overlay = data.overlay;
  const particles = overlay.particles;

  return (
    <>
      {/* Weather event banner */}
      <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[50] max-w-xs" data-testid="weather-overlay-banner">
        <div className="rounded-full bg-black/30 border border-white/10 px-3 py-1 flex items-center gap-2">
          <span className="text-sm animate-pulse">{data.event?.icon || "⚡"}</span>
          <span className="text-white/70 text-[10px] font-bold">{data.event?.name}</span>
          <span className="text-white/40 text-[9px]">{data.minutes_remaining}m</span>
        </div>
      </div>

      {/* Overlay effects */}
      {particles === "meteors" && <MeteorShowerOverlay overlay={overlay} />}
      {particles === "arcs" && <RainbowBridgeOverlay />}
      {particles === "curtains" && <AuroraStormOverlay overlay={overlay} />}
      {particles === "stars" && <StarRainOverlay overlay={overlay} />}
      {particles === "wave" && <CosmicWaveOverlay overlay={overlay} />}
    </>
  );
};

export default WeatherOverlay;
